import { useInterval } from '@/hooks';
import { Box, CurrencyValue } from '@blockanalitica/ui';
import type { CurrencyValueProps } from '@blockanalitica/ui/package/types/src/components/molecules/CurrencyValue/CurrencyValue';
import { useEffect, useMemo, useState } from 'react';

export interface AnimatedCurrencyValueProps extends CurrencyValueProps {
  endlessPerSecond?: number;
  duration?: number;
  animationSpeed?: number;
}

// NOTE: Because of how useInterval (setInterval) works in the background, timings are
// quite inaccurate, but for our showing purposes they should be accurate enough.

export default function AnimatedCurrencyValue({
  value,
  endlessPerSecond = null,
  duration = 1,
  animationSpeed = 50,
  ...rest
}: AnimatedCurrencyValueProps) {
  const number = Number(value);
  const [val, setVal] = useState(number);
  const [num, setNum] = useState(val);
  const [limitStep, setLimitStep] = useState(0);

  useEffect(() => {
    if (val !== number) {
      setVal(number);
      setLimitStep((((number - val) / duration) * animationSpeed) / 1000);
    }
  }, [number, animationSpeed, duration, val]);

  const endlessStep = useMemo(
    () => (endlessPerSecond * animationSpeed) / 1000,
    [endlessPerSecond, animationSpeed]
  );

  const step = useMemo(() => {
    if (limitStep != 0) {
      const wentPast = limitStep < 0 ? num <= val : num >= val;
      if (wentPast) {
        setNum(val);
        setLimitStep(0);
        return 0;
      }
      return limitStep;
    } else {
      return endlessStep;
    }
  }, [limitStep, endlessStep, num, val]);

  useInterval(
    () => {
      setNum(num + step);
    },
    step !== 0 ? animationSpeed : null
  );

  const color = limitStep > 0 ? 'success' : limitStep < 0 ? 'error' : null;

  return (
    <Box color={color}>
      <CurrencyValue value={num} {...rest} />
    </Box>
  );
}
